import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import SummaryPage from "@/pages/SummaryPage";
import UsersPage from "@/pages/UsersPage";
import SessionsPage from "@/pages/SessionsPage";
import MedicalPage from "@/pages/MedicalPage";
import MedicSchedulePage from "@/pages/MedicSchedulePage";
import SchoolBookingPage from "@/pages/SchoolBookingPage";
import SchoolsAdminPage from "@/pages/SchoolsAdminPage";
import { Snowflake } from "lucide-react";

function Protected({ children, adminOnly = false, roles = null }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="auth-loading">
        <div className="card-brutal p-8 flex items-center gap-3">
          <Snowflake className="w-6 h-6 text-sky-500 animate-spin" />
          <span className="font-mono-tight text-xs uppercase tracking-widest font-bold">Loading…</span>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (user.role === "medic" && !["/medical", "/school-schedule"].some((p) => location.pathname.startsWith(p))) {
    return <Navigate to={`/medical?rink=${user.rink || "sligo"}`} replace />;
  }
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/schools/:rink" element={<SchoolBookingPage />} />
            <Route path="/" element={<Protected><Layout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="summary" element={<SummaryPage />} />
              <Route path="schools" element={<SchoolsAdminPage />} />
              <Route path="users" element={<Protected adminOnly><UsersPage /></Protected>} />
              <Route path="sessions" element={<Protected><SessionsPage /></Protected>} />
              <Route path="medical" element={<Protected><MedicalPage /></Protected>} />
              <Route path="school-schedule" element={<Protected roles={["admin", "medic"]}><MedicSchedulePage /></Protected>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" toastOptions={{
          style: { border: "2px solid #0f172a", borderRadius: 0, boxShadow: "4px 4px 0px 0px #0f172a", fontWeight: 700 },
        }} />
      </AuthProvider>
    </div>
  );
}

export default App;
